import { clearStore, iElementsReducer } from "@faro-lotv/project-source";
import { configureStore } from "@reduxjs/toolkit";
import { createReduxEnhancer } from "@sentry/react";
import { activeMembersReducer, resetActiveMembersState } from "@store/active-members/active-members-slice";
import { analyticsReducer, resetAnalyticsState } from "@store/analytics/analytics-slice";
import { appReducer, resetAppState } from "@store/app/app-slice";
import { capturedDataReducer, resetCapturedDataState } from "@store/captured-data/captured-data-slice";
import { companyMemberGroupsReducer, resetCompanyMemberGroupsState } from "@store/company-member-groups/company-member-groups-slice";
import { creditTransactionReducer, resetCreditTransactionState } from "@store/credit-transactions/credit-transactions-slice";
import { devFeaturesReducer, resetDevFeaturesState } from "@store/dev-features/dev-features-slice";
import { errorsReducer, resetErrorsState } from "@store/errors/errors-slice";
import { groupsReducer, resetGroupsState } from "@store/groups/groups-slice";
import { membersReducer, resetMembersState } from "@store/members/members-slice";
import { projectsMapReducer, resetProjectsMapState } from "@store/projects-map/projects-map-slice";
import { projectsReducer, resetProjectsState } from "@store/projects/projects-slice";
import { backgroundTasksReducer, resetSdbBackgroundTasksState } from "@store/sdb-background-tasks/sdb-background-tasks-slice";
import { resetSdbCompanyState, sdbCompanyReducer } from "@store/sdb-company/sdb-company-slice";
import { sdbIElementsReducer } from "@store/sdb-i-elements/sdb-i-elements-slice";
import { resetSettingsState, settingsReducer } from "@store/settings/settings-slice";
import { resetSlideNodesState, slideNodesReducer } from "@store/slide-nodes/slide-nodes-slice";
import { resetTableState, tableReducer } from "@store/table/table-slice";
import { resetTeamState, teamReducer } from "@store/teams/teams-slice";
import { resetUiState, uiReducer } from "@store/ui/ui-slice";
import { resetUploadTasksState, uploadTasksReducer } from "@store/upload-tasks/upload-tasks-slice";
import { resetUserState, userReducer } from "@store/user/user-slice";
export const rootReducer = {
  // When adding a new reducer here also add the corresponding reset action to the "resetStore" function.
  analytics: analyticsReducer,
  capturedData: capturedDataReducer,
  companyMemberGroups: companyMemberGroupsReducer,
  errors: errorsReducer,
  groups: groupsReducer,
  iElements: iElementsReducer,
  members: membersReducer,
  projects: projectsReducer,
  projectsMap: projectsMapReducer,
  sdbBackgroundTasks: backgroundTasksReducer,
  sdbCompany: sdbCompanyReducer,
  sdbIElements: sdbIElementsReducer,
  settings: settingsReducer,
  slideNodes: slideNodesReducer,
  table: tableReducer,
  ui: uiReducer,
  uploadTasks: uploadTasksReducer,
  user: userReducer,
  teams: teamReducer,
  creditTransactions: creditTransactionReducer,
  activeMembers: activeMembersReducer,
  app: appReducer,
  devFeatures: devFeaturesReducer
};

/**
 * Sentry's Redux enhancer. It captures all actions and state changes.
 * You can pass options to filter out sensitive user data
 * DOC: https://docs.sentry.io/platforms/javascript/guides/react/features/redux/
 */
const sentryReduxEnhancer = createReduxEnhancer({
  /**
   * Update State and only send minimum data that might be useful for tracking error.
   * There is no need to pollute the Sentry with all the Sentry data. We can later add more data if needed.
   */
  stateTransformer: state => {
    const transformedState = {
      ui: state.ui,
      app: state.app
    };
    return transformedState;
  }
});
export const store = configureStore({
  reducer: rootReducer,
  enhancers: getDefaultEnhancers => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  }
});

/**
 * Resets the store. Dispatches all reducers reset actions. It can be called from any component.
 * For every new store slice added it's corresponding reset action must be added here.
 *
 * @param dispatch AppDispatch
 */
export function resetStore(dispatch) {
  dispatch(resetAnalyticsState());
  dispatch(resetCapturedDataState());
  dispatch(resetCompanyMemberGroupsState());
  dispatch(resetErrorsState());
  dispatch(resetGroupsState());
  dispatch(resetMembersState());
  dispatch(resetProjectsState());
  dispatch(resetProjectsMapState());
  dispatch(resetSdbBackgroundTasksState());
  dispatch(resetSdbCompanyState());
  dispatch(resetSettingsState());
  dispatch(resetSlideNodesState());
  dispatch(resetTableState());
  dispatch(resetUiState());
  dispatch(resetUserState());
  dispatch(resetUploadTasksState());
  dispatch(resetTeamState());
  dispatch(resetCreditTransactionState());
  dispatch(resetActiveMembersState());
  dispatch(resetAppState());
  dispatch(resetDevFeaturesState());
  // `clearStore` is the reset action for the iElementsReducer store slice and for all other store slices
  // imported from the @faro-lotv/project-source package
  dispatch(clearStore());
}